/* Design Lab — standalone "Modern Enterprise" redesign showcase.
   Route: /design-lab. Not linked anywhere in the app — preview only. */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  PhoneCall,
  Banknote,
  ShieldCheck,
  Sparkles,
  BadgeCheck,
  Zap,
  Lock,
  RefreshCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";

const NAV_LINKS = ["Features", "Publishers", "Buyers", "Compliance", "Live Metrics"];

const PARTNERS = [
  { name: "Ringba", role: "Call Tracking Integration" },
  { name: "Retreaver", role: "Routing Partner" },
  { name: "Anura", role: "Fraud Protection" },
  { name: "Invoca", role: "Conversation Intelligence" },
  { name: "TrackDrive", role: "Campaign Management" },
  { name: "TCPA Shield", role: "Verification Partner" },
];

const TRUST_BADGES = [
  { icon: BadgeCheck, label: "100% TCPA Certified" },
  { icon: Zap, label: "Real-Time Fraud Filter" },
  { icon: Lock, label: "SOC2 Compliant Architecture" },
  { icon: RefreshCcw, label: "Weekly Payout Guarantee" },
];

const FEATURES = [
  {
    icon: Banknote,
    badge: "Fintech Grade",
    title: "Weekly Guaranteed Payouts",
    description:
      "Never wait on cash flow. Fast, reliable weekly settlements via ACH, Wire, or Crypto with real-time payout tracking.",
    highlight: "Net-7 Terms Available",
  },
  {
    icon: PhoneCall,
    badge: "0% Bot Traffic",
    title: "Verified High-Intent Live Calls",
    description:
      "Pre-qualified inbound callers routed instantly with dynamic IVR qualification and sub-second ping-post bidding.",
    highlight: "TCPA Direct-Consent",
  },
  {
    icon: ShieldCheck,
    badge: "AI Guard",
    title: "Real-Time Analytics & Anti-Fraud",
    description:
      "Granular caller telemetry, call recordings, conversion attribution, and AI fraud blocking active 24/7.",
    highlight: "99.9% Route Uptime",
  },
];

const STATS = [
  { value: "50K+", label: "Qualified Calls / Month" },
  { value: "99.9%", label: "Routing Uptime" },
  { value: "Net-7", label: "Fastest Payout Terms" },
  { value: "24/7", label: "Fraud Monitoring" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function DesignLabPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-['Inter',-apple-system,sans-serif] antialiased">
      {/* ── Glassmorphism Navbar ─────────────────────────────── */}
      <header
        data-testid="design-lab-nav"
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-slate-200/80 transition-all duration-300"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-md shadow-blue-500/20">
              <PhoneCall className="w-4 h-4 text-white" strokeWidth={2.25} />
            </div>
            <div className="leading-tight">
              <p className="font-['Poppins'] font-bold text-[15px] tracking-tight">
                Digital Call Network
              </p>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-blue-600 flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-60" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-600" />
                </span>
                Enterprise Preview
              </p>
            </div>
          </div>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => (
              <a
                key={link}
                href="#design-lab-features"
                data-testid={`design-lab-nav-${link.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                {link}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              data-testid="design-lab-login-btn"
              className="hidden sm:inline-flex rounded-xl border-slate-300 hover:border-blue-400 hover:text-blue-600 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
            >
              Login
            </Button>
            <Button
              data-testid="design-lab-nav-apply-btn"
              className="rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-md shadow-blue-500/15 hover:shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-200"
            >
              Apply Now
            </Button>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Animated brand-gradient backdrop */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-slate-100" />
          <motion.div
            className="absolute -top-40 -left-32 w-[34rem] h-[34rem] rounded-full bg-blue-500/25 blur-3xl"
            animate={{ x: [0, 60, 0], y: [0, 40, 0] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-10 right-[-10rem] w-[30rem] h-[30rem] rounded-full bg-purple-500/25 blur-3xl"
            animate={{ x: [0, -50, 0], y: [0, 60, 0] }}
            transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-[-12rem] left-1/3 w-[26rem] h-[26rem] rounded-full bg-cyan-400/20 blur-3xl"
            animate={{ x: [0, 40, 0], y: [0, -40, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 sm:pt-28 sm:pb-24 text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <span
              data-testid="design-lab-hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/70 backdrop-blur-md px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-blue-700 shadow-sm"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Modern Enterprise Showcase · /design-lab
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={1}
            className="mt-7 font-['Poppins'] text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl mx-auto"
          >
            High-Intent Pay-Per-Call Network{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 bg-clip-text text-transparent">
              Built for Enterprise Scale
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={2}
            className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto"
          >
            Connect directly to verified, TCPA-compliant live phone leads with
            real-time analytics, instant call routing, and guaranteed weekly payouts.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={3}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              size="lg"
              data-testid="design-lab-hero-publisher-btn"
              className="h-12 px-8 rounded-xl text-base bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-md shadow-blue-500/15 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5 hover:scale-[1.02] active:translate-y-0 active:scale-[0.98] transition-all duration-200"
            >
              Join Network as Publisher
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              data-testid="design-lab-hero-buyer-btn"
              className="h-12 px-8 rounded-xl text-base bg-white/60 backdrop-blur-md border-slate-300 hover:border-blue-400 hover:bg-white/80 hover:text-blue-700 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:scale-[0.98] transition-all duration-200"
            >
              <PhoneCall className="w-4 h-4" />
              Request Buyer Access
            </Button>
          </motion.div>

          {/* Stats bar */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            custom={4}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl overflow-hidden border border-slate-200/80 bg-slate-200/60 backdrop-blur-sm max-w-3xl mx-auto shadow-sm"
            data-testid="design-lab-stats-bar"
          >
            {STATS.map((s) => (
              <div key={s.label} className="bg-white/80 backdrop-blur-md px-4 py-5">
                <p className="font-['Poppins'] text-2xl font-bold tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {s.value}
                </p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Trust Section ────────────────────────────────────── */}
      <section className="py-16 sm:py-20 border-t border-slate-200/70 bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600 text-center">
            Ecosystem
          </p>
          <h2 className="mt-3 font-['Poppins'] text-2xl sm:text-3xl font-bold tracking-tight text-center">
            Trusted by Top Telecom Platforms &amp; Enterprise Buyers
          </h2>

          {/* Partner logo placeholders */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="design-lab-partner-grid">
            {PARTNERS.map((p, i) => (
              <motion.div
                key={p.name}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                custom={i * 0.5}
                className="group rounded-xl border border-slate-200 bg-white/70 backdrop-blur-md px-4 py-6 text-center hover:border-blue-300 hover:shadow-lg hover:shadow-blue-500/10 hover:-translate-y-1 transition-all duration-200"
                data-testid={`design-lab-partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="mx-auto w-10 h-10 rounded-lg bg-slate-100 group-hover:bg-blue-50 flex items-center justify-center transition-colors">
                  <span className="font-['Poppins'] text-sm font-bold text-slate-400 group-hover:text-blue-600 transition-colors">
                    {p.name.slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <p className="mt-3 text-sm font-semibold text-slate-700">{p.name}</p>
                <p className="mt-0.5 text-[11px] text-slate-400">{p.role}</p>
              </motion.div>
            ))}
          </div>

          {/* Compliance badges */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-testid="design-lab-trust-badges">
            {TRUST_BADGES.map((b) => (
              <span
                key={b.label}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 backdrop-blur-sm px-4 py-2 text-xs font-semibold text-slate-600 shadow-sm hover:border-emerald-300 hover:text-emerald-700 transition-colors"
              >
                <b.icon className="w-3.5 h-3.5 text-emerald-500" />
                {b.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3-Column Feature Grid ────────────────────────────── */}
      <section id="design-lab-features" className="py-16 sm:py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">
              Why Digital Call Network
            </p>
            <h2 className="mt-3 font-['Poppins'] text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Enterprise Infrastructure, Publisher-First Economics
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Everything engineered for serious media buyers — from instant call
              routing to guaranteed weekly settlements.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" data-testid="design-lab-feature-grid">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                className="group relative rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-md p-8 shadow-sm hover:shadow-xl hover:shadow-blue-500/10 hover:-translate-y-1.5 hover:border-blue-200 transition-all duration-300"
                data-testid={`design-lab-feature-${i}`}
              >
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-md shadow-blue-500/20 group-hover:scale-110 transition-transform duration-300">
                    <f.icon className="w-5 h-5 text-white" strokeWidth={2.25} />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider rounded-full bg-blue-50 border border-blue-100 text-blue-700 px-2.5 py-1">
                    {f.badge}
                  </span>
                </div>
                <h3 className="mt-6 font-['Poppins'] text-xl font-semibold tracking-tight">
                  {f.title}
                </h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.description}</p>
                <p className="mt-5 text-xs font-semibold text-blue-600 flex items-center gap-1.5">
                  <BadgeCheck className="w-3.5 h-3.5" />
                  {f.highlight}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer note ──────────────────────────────────────── */}
      <footer className="border-t border-slate-200/70 bg-white/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            Design Lab — preview build for Digital Master Media LLC. Not linked in production navigation.
          </p>
          <Link
            to="/"
            data-testid="design-lab-back-home"
            className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
          >
            ← Back to current site
          </Link>
        </div>
      </footer>
    </div>
  );
}
